"use client";
import React from "react";
import { NodeModel } from "@minoru/react-dnd-treeview";
import { WcStack, WcTypography } from "@bpipartners/webui";
import { useTheme } from "@guberno/utils/theme";
import { useTranslations } from "next-intl";
import { RenderDrawerIcons } from "./RenderDrawerIcons";
import {
  MdOutlineKeyboardArrowDown,
  MdOutlineKeyboardArrowUp,
} from "react-icons/md";

interface CustomDrawerNodeProps {
  node: NodeModel;
  depth: number;
  isSelected: boolean;
  hasChild: boolean;
  isChildItem: boolean;
  isParentOfSelectedChild: boolean;
  onToggle: (id: string | number) => void;
  isOpen: boolean;
  onSelect: (node: NodeModel) => void;
  isDrawerExpaned: boolean;
}

export const CustomDrawerNode = ({
  onToggle,
  depth,
  isOpen,
  node,
  onSelect,
  isSelected,
  hasChild,
  isChildItem = false,
  isParentOfSelectedChild,
  isDrawerExpaned,
}: CustomDrawerNodeProps) => {
  const { id, data } = node;

  const t = useTranslations("common");
  const theme = useTheme();
  const handleToggle = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onToggle(node.id);
  };

  const handleSelect = () => onSelect(node);

  return (
    <WcStack
      id={`CustomDrawerNode${id}`}
      direction={"row"}
      sx={{
        width: "100%",
        justifyContent: isDrawerExpaned ? "flex-start" : "center",
        borderRadius: 2,
        padding: 4,
        alignItems: "center",
        cursor: "pointer",
        boxShadow:
          (isSelected && !isChildItem) || isParentOfSelectedChild
            ? `inset 4px 0 0 ${theme.appColors.primary}`
            : "none",
        backgroundColor:
          (isSelected && !isChildItem) || isParentOfSelectedChild
            ? theme.appColors.surfaceContainer
            : "inherit",
        "&:hover": {
          background: !isChildItem
            ? theme.appColors.surfaceContainerLow
            : "inherit",
        },
      }}
      height={theme.dimensions.appDrawerItemHeight}
      marginLeft={isChildItem ? "20px" : 0}
    >
      <WcStack
        direction={"row"}
        width={"100%"}
        alignItems={"center"}
        justifyContent={isDrawerExpaned ? "inherit" : "center"}
      >
        <WcStack
          width={isDrawerExpaned ? "100%" : "auto"}
          spacing={5}
          direction={"row"}
          alignItems={"center"}
          onClick={(event) => {
            //@ts-ignore
            if (data.type !== "group") {
              handleSelect();
            } else {
              handleToggle(event);
            }
          }}
          sx={{
            color: isSelected
              ? theme.appColors.primary
              : theme.appPalette.secondary[30],
            "&:hover": {
              color: theme.appPalette.secondary[10],
            },
          }}
        >
          <WcStack paddingLeft={isDrawerExpaned ? 0 : 2}>
            {
              //@ts-ignore
              data.icon !== "" && <RenderDrawerIcons itemType={node.text} />
            }
          </WcStack>
          {isDrawerExpaned && (
            <WcTypography variant={isChildItem ? "body2" : "body1"}>
              {t(node.text)}
            </WcTypography>
          )}
        </WcStack>
      </WcStack>

      {hasChild &&
        isDrawerExpaned &&
        (isOpen ? (
          <WcStack onClick={handleToggle}>
            <MdOutlineKeyboardArrowUp />
          </WcStack>
        ) : (
          <WcStack onClick={handleToggle}>
            <MdOutlineKeyboardArrowDown />
          </WcStack>
        ))}
    </WcStack>
  );
};
